/**
 * MA-KNOWLEDGE-ARCH-001 + MA-KNOWLEDGE-EXTRACT-002
 * Business Knowledge subsection for Foundation Step
 *
 * First-class subsection with:
 *  - Three note textareas with debounced auto-save via dedicated knowledge API
 *  - File upload with drag-and-drop, real persistence
 *  - AI extraction pipeline: upload → extract → review → apply/merge/dismiss
 *  - Knowledge Strength indicator (accounts for extraction state)
 *  - Compliance guardrail: compliance suggestions are review-only, never auto-applied
 */

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronRight,
  Upload,
  FileText,
  Loader2,
  Check,
  Sparkles,
  Trash2,
  Zap,
  Eye,
  ArrowRight,
  Merge,
  X,
  AlertTriangle,
  ListChecks,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";

// ─── Constants ────────────────────────────────────────────────────────────
const FILE_TYPE_OPTIONS = [
  { value: "product_sheet", label: "Product Sheet" },
  { value: "compliance_doc", label: "Compliance Doc" },
  { value: "faq_doc", label: "FAQ Document" },
  { value: "sales_process", label: "Sales Process" },
  { value: "training_doc", label: "Training Doc" },
  { value: "other", label: "Other" },
];

const STRENGTH_LABELS = [
  { max: 0, label: "Empty", color: "text-slate-500" },
  { max: 30, label: "Getting started", color: "text-amber-400" },
  { max: 55, label: "Partial", color: "text-amber-300" },
  { max: 80, label: "Good", color: "text-emerald-400" },
  { max: 100, label: "Strong", color: "text-emerald-300" },
];

function getStrengthMeta(score) {
  for (const s of STRENGTH_LABELS) {
    if (score <= s.max) return s;
  }
  return STRENGTH_LABELS[STRENGTH_LABELS.length - 1];
}

function formatFileSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Debounce hook ────────────────────────────────────────────────────────
function useDebouncedSave(saveFn, delay = 1200) {
  const timer = useRef(null);
  const latest = useRef(null);
  const trigger = useCallback(
    (payload) => {
      latest.current = payload;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => saveFn(latest.current), delay);
    },
    [saveFn, delay]
  );
  useEffect(() => () => clearTimeout(timer.current), []);
  return trigger;
}

// ═══════════════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════════════
export function BusinessKnowledge({ profitPathId }) {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(true);
  const [notes, setNotes] = useState({
    business_notes: "",
    compliance_notes: "",
    faq_objection_notes: "",
  });
  const [saveStatus, setSaveStatus] = useState("idle");
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [reviewFileId, setReviewFileId] = useState(null);
  const fileInputRef = useRef(null);
  const initialized = useRef(false);

  // ── Fetch knowledge overview ──────────────────────────────────────────
  const { data: knowledge, isLoading } = useQuery({
    queryKey: ["knowledge", profitPathId],
    queryFn: () =>
      api.get(`/profit-paths/${profitPathId}/knowledge`).then((r) => r.data),
    enabled: !!profitPathId,
    staleTime: 10_000,
    refetchInterval: (query) => {
      // Poll faster if any file is extracting
      const files = query.state.data?.files ?? [];
      const hasExtracting = files.some(
        (f) => f.extraction_status === "pending" || f.extraction_status === "extracting"
      );
      return hasExtracting ? 4000 : 30000;
    },
  });

  useEffect(() => {
    if (knowledge && !initialized.current) {
      setNotes({
        business_notes: knowledge.business_notes || "",
        compliance_notes: knowledge.compliance_notes || "",
        faq_objection_notes: knowledge.faq_objection_notes || "",
      });
      initialized.current = true;
    }
  }, [knowledge]);

  // Re-sync notes when extraction apply changes them
  const notesVersion = `${knowledge?.business_notes ?? ""}|${knowledge?.compliance_notes ?? ""}|${knowledge?.faq_objection_notes ?? ""}`;
  const prevNotesVersion = useRef(notesVersion);
  useEffect(() => {
    if (initialized.current && notesVersion !== prevNotesVersion.current) {
      setNotes({
        business_notes: knowledge?.business_notes || "",
        compliance_notes: knowledge?.compliance_notes || "",
        faq_objection_notes: knowledge?.faq_objection_notes || "",
      });
      prevNotesVersion.current = notesVersion;
    }
  }, [notesVersion, knowledge]);

  // ── Save notes ────────────────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: (payload) =>
      api.put(`/profit-paths/${profitPathId}/knowledge`, payload),
    onMutate: () => setSaveStatus("saving"),
    onSuccess: () => {
      setSaveStatus("saved");
      queryClient.invalidateQueries({ queryKey: ["knowledge", profitPathId] });
      setTimeout(() => setSaveStatus("idle"), 2000);
    },
    onError: () => setSaveStatus("idle"),
  });

  const debouncedSave = useDebouncedSave(
    useCallback((p) => saveMutation.mutate(p), [saveMutation])
  );

  const handleNoteChange = useCallback(
    (field, value) => {
      setNotes((prev) => {
        const next = { ...prev, [field]: value };
        debouncedSave(next);
        return next;
      });
    },
    [debouncedSave]
  );

  // ── File upload + extraction trigger ──────────────────────────────────
  const uploadMutation = useMutation({
    mutationFn: async ({ file, title, knowledgeType }) => {
      const form = new FormData();
      form.append("file", file);
      form.append("title", title);
      form.append("knowledge_type", knowledgeType);
      const res = await api.post(
        `/profit-paths/${profitPathId}/knowledge-files`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["knowledge", profitPathId] });
      // Trigger extraction as a separate fire-and-forget call
      if (data?.id && data.upload_status === "completed") {
        api
          .post(`/knowledge-files/${data.id}/extract`)
          .catch(() => {}); // extraction failure is non-blocking
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (fileId) =>
      api.delete(`/profit-paths/${profitPathId}/knowledge-files/${fileId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["knowledge", profitPathId] });
    },
  });

  const processFiles = useCallback(
    async (fileList) => {
      const files = Array.from(fileList);
      for (const file of files) {
        const tempId = `temp_${Date.now()}_${file.name}`;
        setUploadingFiles((prev) => [...prev, { id: tempId, name: file.name }]);
        try {
          const title = file.name.replace(/\.[^.]+$/, "");
          await uploadMutation.mutateAsync({ file, title, knowledgeType: "other" });
        } finally {
          setUploadingFiles((prev) => prev.filter((f) => f.id !== tempId));
        }
      }
    },
    [uploadMutation]
  );

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragActive(false);
      if (e.dataTransfer.files?.length) processFiles(e.dataTransfer.files);
    },
    [processFiles]
  );

  // ── Derived ───────────────────────────────────────────────────────────
  const strength = knowledge?.knowledge_strength ?? 0;
  const strengthMeta = getStrengthMeta(strength);
  const files = knowledge?.files ?? [];

  if (!profitPathId) return null;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div
        className="mt-6 border border-white/[0.06] rounded-lg overflow-hidden"
        data-testid="business-knowledge-section"
      >
        {/* ─── Header ──────────────────────────────────────────────── */}
        <CollapsibleTrigger asChild>
          <button
            className="w-full flex items-center justify-between px-4 py-3 bg-slate-800/40 hover:bg-slate-800/60 transition-colors"
            data-testid="business-knowledge-toggle"
          >
            <div className="flex items-center gap-2.5">
              <span className="text-sm font-medium text-slate-200">
                Business Knowledge
              </span>
              <span className="text-[10px] text-slate-500 uppercase tracking-wider">
                optional
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <div className="w-16 h-1.5 rounded-full bg-slate-700/60 overflow-hidden">
                  <div
                    className={cn(
                      "h-full rounded-full transition-all duration-500",
                      strength > 70
                        ? "bg-emerald-500"
                        : strength > 30
                        ? "bg-amber-500"
                        : strength > 0
                        ? "bg-amber-600"
                        : "bg-slate-600"
                    )}
                    style={{ width: `${strength}%` }}
                  />
                </div>
                <span
                  className={cn("text-[10px] font-medium min-w-[60px]", strengthMeta.color)}
                  data-testid="knowledge-strength-label"
                >
                  {strengthMeta.label}
                </span>
              </div>
              {saveStatus === "saving" && (
                <Loader2 className="h-3 w-3 text-slate-400 animate-spin" />
              )}
              {saveStatus === "saved" && (
                <Check className="h-3 w-3 text-emerald-400" />
              )}
              <ChevronDown
                className={cn(
                  "h-4 w-4 text-slate-500 transition-transform duration-200",
                  isOpen && "rotate-180"
                )}
              />
            </div>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-4 pt-3 pb-4 space-y-5 border-t border-white/[0.04]">
            {/* Explanation */}
            <div className="flex items-start gap-2 p-2.5 rounded-md bg-indigo-500/[0.06] border border-indigo-500/10">
              <Sparkles className="h-3.5 w-3.5 text-indigo-400 mt-0.5 shrink-0" />
              <p className="text-[11px] text-slate-400 leading-relaxed">
                Knowledge you add here grounds every AI-generated asset. Upload
                documents and the AI will extract structured suggestions you can
                review and apply.
              </p>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 text-slate-500 animate-spin" />
              </div>
            ) : (
              <>
                {/* ─── Notes ───────────────────────────────────────── */}
                <NoteField
                  id="knowledge-business"
                  label="Business / Product Notes"
                  hint="Product facts, differentiators, pricing, competitive advantages"
                  placeholder="e.g., Our Medicare Advantage plans cover $0 copay for primary care..."
                  value={notes.business_notes}
                  onChange={(v) => handleNoteChange("business_notes", v)}
                  testId="knowledge-business-notes"
                />
                <NoteField
                  id="knowledge-compliance"
                  label="Compliance & Claims Guidance"
                  hint="Forbidden claims, required disclaimers, regulatory safe wording"
                  placeholder="e.g., Never guarantee specific savings amounts..."
                  value={notes.compliance_notes}
                  onChange={(v) => handleNoteChange("compliance_notes", v)}
                  testId="knowledge-compliance-notes"
                />
                <NoteField
                  id="knowledge-faq"
                  label="FAQs / Common Objections"
                  hint="Frequent objections, misconceptions, how to handle them"
                  placeholder="e.g., 'I already have Medicare' — Many don't realize..."
                  value={notes.faq_objection_notes}
                  onChange={(v) => handleNoteChange("faq_objection_notes", v)}
                  testId="knowledge-faq-notes"
                />

                {/* ─── Supporting Documents ─────────────────────────── */}
                <div className="space-y-2.5">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs font-medium text-slate-300">
                      Supporting Documents
                    </Label>
                    {files.length > 0 && (
                      <Badge
                        variant="secondary"
                        className="h-4 px-1.5 text-[10px] bg-slate-700/50 text-slate-400 border-0"
                      >
                        {files.length}
                      </Badge>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 -mt-1">
                    Upload documents — AI will automatically extract structured
                    knowledge for your review.
                  </p>

                  {/* Drop zone */}
                  <div
                    className={cn(
                      "relative border border-dashed rounded-md px-4 py-5 text-center transition-colors cursor-pointer",
                      dragActive
                        ? "border-violet-500/50 bg-violet-500/[0.06]"
                        : "border-slate-700/50 bg-slate-800/20 hover:border-slate-600/50"
                    )}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragActive(true);
                    }}
                    onDragLeave={() => setDragActive(false)}
                    onDrop={onDrop}
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="knowledge-file-dropzone"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      multiple
                      accept=".pdf,.doc,.docx,.txt,.csv,.jpg,.jpeg,.png,.webp"
                      onChange={(e) => {
                        if (e.target.files?.length) processFiles(e.target.files);
                        e.target.value = "";
                      }}
                    />
                    <Upload
                      className={cn(
                        "h-5 w-5 mx-auto mb-1.5",
                        dragActive ? "text-violet-400" : "text-slate-500"
                      )}
                    />
                    <p className="text-xs text-slate-400">
                      Drop files here or{" "}
                      <span className="text-indigo-400 font-medium">browse</span>
                    </p>
                    <p className="text-[10px] text-slate-600 mt-0.5">
                      PDF, Word, TXT, CSV, images — up to 20 MB each
                    </p>
                  </div>

                  {/* Uploading */}
                  {uploadingFiles.map((f) => (
                    <div
                      key={f.id}
                      className="flex items-center gap-2 px-3 py-2 rounded-md bg-slate-800/40 border border-slate-700/30"
                    >
                      <Loader2 className="h-3.5 w-3.5 text-violet-400 animate-spin shrink-0" />
                      <span className="text-xs text-slate-300 truncate flex-1">
                        {f.name}
                      </span>
                      <span className="text-[10px] text-slate-500">Uploading...</span>
                    </div>
                  ))}

                  {/* File list */}
                  {files.length > 0 && (
                    <div className="space-y-1.5" data-testid="knowledge-files-list">
                      {files.map((file) => (
                        <FileRowWithExtraction
                          key={file.id}
                          file={file}
                          profitPathId={profitPathId}
                          isReviewing={reviewFileId === file.id}
                          onToggleReview={() =>
                            setReviewFileId((prev) =>
                              prev === file.id ? null : file.id
                            )
                          }
                          onDelete={() => deleteMutation.mutate(file.id)}
                          isDeleting={
                            deleteMutation.isPending &&
                            deleteMutation.variables === file.id
                          }
                          onApplied={() => {
                            setReviewFileId(null);
                            // Reset initialized flag so notes re-sync from server
                            initialized.current = false;
                            queryClient.invalidateQueries({
                              queryKey: ["knowledge", profitPathId],
                            });
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// NoteField
// ═══════════════════════════════════════════════════════════════════════════
function NoteField({ id, label, hint, placeholder, value, onChange, testId }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs font-medium text-slate-300">{label}</Label>
      <p className="text-[11px] text-slate-500 -mt-0.5">{hint}</p>
      <Textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="min-h-[72px] text-sm bg-slate-800/40 border-slate-700/40 text-slate-100 placeholder:text-slate-600 resize-none focus:border-violet-500/30 focus:ring-violet-500/10"
        data-testid={testId}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// FileRowWithExtraction
// ═══════════════════════════════════════════════════════════════════════════
function FileRowWithExtraction({
  file,
  profitPathId,
  isReviewing,
  onToggleReview,
  onDelete,
  isDeleting,
  onApplied,
}) {
  const extractionStatus = file.extraction_status || "none";
  const applyStatus = file.extraction_apply_status || "not_applied";

  const typeLabel =
    FILE_TYPE_OPTIONS.find((o) => o.value === file.knowledge_type)?.label ??
    file.knowledge_type;

  // Status badge config
  const statusConfig = useMemo(() => {
    if (extractionStatus === "extracting" || extractionStatus === "pending") {
      return { label: "Extracting...", cls: "bg-violet-500/15 text-violet-400", animate: true };
    }
    if (extractionStatus === "failed") {
      return { label: "Extraction Failed", cls: "bg-red-500/15 text-red-400" };
    }
    if (applyStatus === "applied") {
      return { label: "Applied", cls: "bg-emerald-500/15 text-emerald-400" };
    }
    if (applyStatus === "merged") {
      return { label: "Merged", cls: "bg-emerald-500/15 text-emerald-400" };
    }
    if (applyStatus === "dismissed") {
      return { label: "Dismissed", cls: "bg-slate-500/15 text-slate-400" };
    }
    if (extractionStatus === "completed") {
      return { label: "Ready for Review", cls: "bg-amber-500/15 text-amber-300" };
    }
    if (file.upload_status === "completed") {
      return { label: "Uploaded", cls: "bg-slate-500/15 text-slate-400" };
    }
    return { label: file.upload_status, cls: "bg-slate-500/15 text-slate-400" };
  }, [extractionStatus, applyStatus, file.upload_status]);

  const canReview =
    extractionStatus === "completed" &&
    applyStatus !== "applied" &&
    applyStatus !== "merged";

  return (
    <div data-testid={`knowledge-file-${file.id}`}>
      <div
        className={cn(
          "group flex items-center gap-2 px-3 py-2 rounded-md border transition-colors",
          isReviewing
            ? "bg-slate-800/50 border-violet-500/20"
            : "bg-slate-800/30 border-slate-700/20 hover:border-slate-700/40"
        )}
      >
        <FileText className="h-3.5 w-3.5 text-slate-500 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-slate-300 truncate">{file.title}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] text-slate-500">{typeLabel}</span>
            {file.file_size && (
              <>
                <span className="text-[10px] text-slate-600">·</span>
                <span className="text-[10px] text-slate-500">
                  {formatFileSize(file.file_size)}
                </span>
              </>
            )}
          </div>
        </div>

        <Badge
          variant="secondary"
          className={cn("h-4 px-1.5 text-[9px] border-0 shrink-0", statusConfig.cls)}
        >
          {statusConfig.animate && (
            <Loader2 className="h-2.5 w-2.5 mr-0.5 animate-spin" />
          )}
          {statusConfig.label}
        </Badge>

        {canReview && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleReview();
            }}
            className="flex items-center gap-1 h-5 px-2 rounded text-[10px] font-medium bg-violet-500/15 text-violet-400 hover:bg-violet-500/25 transition-colors"
            data-testid={`knowledge-file-review-${file.id}`}
          >
            <Eye className="h-2.5 w-2.5" />
            Review
          </button>
        )}

        {(applyStatus === "applied" || applyStatus === "merged") && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleReview();
            }}
            className="flex items-center gap-1 h-5 px-2 rounded text-[10px] font-medium bg-slate-700/30 text-slate-400 hover:bg-slate-700/50 transition-colors"
          >
            <Eye className="h-2.5 w-2.5" />
            View
          </button>
        )}

        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          disabled={isDeleting}
          className="opacity-0 group-hover:opacity-100 h-5 w-5 flex items-center justify-center rounded hover:bg-red-500/20 transition-all"
          data-testid={`knowledge-file-delete-${file.id}`}
        >
          {isDeleting ? (
            <Loader2 className="h-3 w-3 text-red-400 animate-spin" />
          ) : (
            <Trash2 className="h-3 w-3 text-red-400" />
          )}
        </button>
      </div>

      {/* Extraction Review Panel */}
      {isReviewing && (
        <ExtractionReviewPanel
          fileId={file.id}
          profitPathId={profitPathId}
          onApplied={onApplied}
          onClose={() => onToggleReview()}
          alreadyApplied={applyStatus === "applied" || applyStatus === "merged"}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ExtractionReviewPanel
// ═══════════════════════════════════════════════════════════════════════════
function ExtractionReviewPanel({ fileId, profitPathId, onApplied, onClose, alreadyApplied }) {
  const queryClient = useQueryClient();
  const [applyBusiness, setApplyBusiness] = useState(true);
  const [applyCompliance, setApplyCompliance] = useState(false); // OFF by default — compliance guardrail
  const [applyFaq, setApplyFaq] = useState(true);

  const { data: extraction, isLoading } = useQuery({
    queryKey: ["extraction", fileId],
    queryFn: () =>
      api.get(`/knowledge-files/${fileId}/extraction`).then((r) => r.data),
    enabled: !!fileId,
  });

  const applyMutation = useMutation({
    mutationFn: (payload) =>
      api.post(`/knowledge-files/${fileId}/extraction/apply`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["knowledge", profitPathId] });
      queryClient.invalidateQueries({ queryKey: ["extraction", fileId] });
      onApplied();
    },
  });

  if (isLoading) {
    return (
      <div className="ml-5 mt-1.5 p-4 rounded-md bg-slate-800/30 border border-slate-700/20">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 text-violet-400 animate-spin" />
          <span className="text-xs text-slate-400">Loading extraction...</span>
        </div>
      </div>
    );
  }

  if (!extraction) return null;

  const confidence = extraction.confidence_score ?? 0;
  const confidenceColor =
    confidence >= 0.8 ? "text-emerald-400" : confidence >= 0.5 ? "text-amber-400" : "text-red-400";

  return (
    <div
      className="ml-1 mt-1.5 rounded-md bg-slate-900/60 border border-violet-500/10 overflow-hidden"
      data-testid={`extraction-review-${fileId}`}
    >
      {/* Review Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-violet-500/[0.04] border-b border-violet-500/10">
        <div className="flex items-center gap-2">
          <Zap className="h-3.5 w-3.5 text-violet-400" />
          <span className="text-xs font-medium text-slate-200">
            AI Extraction Result
          </span>
          <span className={cn("text-[10px] font-medium", confidenceColor)}>
            {Math.round(confidence * 100)}% confidence
          </span>
        </div>
        <button
          onClick={onClose}
          className="h-5 w-5 flex items-center justify-center rounded hover:bg-slate-700/40"
        >
          <X className="h-3 w-3 text-slate-400" />
        </button>
      </div>

      <div className="p-3 space-y-3">
        {/* Summary */}
        {extraction.summary && (
          <div className="text-[11px] text-slate-300 leading-relaxed bg-slate-800/30 p-2 rounded">
            {extraction.summary}
          </div>
        )}

        {/* Extracted categories */}
        <div className="grid grid-cols-2 gap-2">
          <ExtractedList
            title="Business Facts"
            items={extraction.business_product_facts}
          />
          <ExtractedList
            title="Differentiators"
            items={extraction.differentiators}
          />
          <ExtractedList
            title="Compliance Flags"
            items={extraction.compliance_flags}
            isCompliance
          />
          <ExtractedList
            title="FAQ / Objections"
            items={extraction.faq_objection_insights}
          />
        </div>

        {/* Suggested notes */}
        {!alreadyApplied && (
          <div className="space-y-2 pt-2 border-t border-white/[0.04]">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">
              Suggested Note Content
            </p>

            {extraction.suggested_business_notes && (
              <SuggestedNote
                label="Business / Product Notes"
                text={extraction.suggested_business_notes}
                checked={applyBusiness}
                onToggle={() => setApplyBusiness((p) => !p)}
                testId="apply-business-toggle"
              />
            )}

            {extraction.suggested_compliance_notes && (
              <SuggestedNote
                label="Compliance & Claims Guidance"
                text={extraction.suggested_compliance_notes}
                checked={applyCompliance}
                onToggle={() => setApplyCompliance((p) => !p)}
                isCompliance
                testId="apply-compliance-toggle"
              />
            )}

            {extraction.suggested_faq_notes && (
              <SuggestedNote
                label="FAQs / Common Objections"
                text={extraction.suggested_faq_notes}
                checked={applyFaq}
                onToggle={() => setApplyFaq((p) => !p)}
                testId="apply-faq-toggle"
              />
            )}

            {/* Action buttons */}
            <div className="flex items-center gap-2 pt-2">
              <Button
                size="sm"
                className="h-7 px-3 text-[11px] bg-violet-600 hover:bg-violet-500 text-white"
                onClick={() =>
                  applyMutation.mutate({
                    mode: "replace",
                    apply_business: applyBusiness,
                    apply_compliance: applyCompliance,
                    apply_faq: applyFaq,
                  })
                }
                disabled={applyMutation.isPending || (!applyBusiness && !applyCompliance && !applyFaq)}
                data-testid="extraction-apply-btn"
              >
                {applyMutation.isPending ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <ArrowRight className="h-3 w-3 mr-1" />
                )}
                Apply to Notes
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-3 text-[11px] border-slate-700 text-slate-300 hover:bg-slate-800"
                onClick={() =>
                  applyMutation.mutate({
                    mode: "merge",
                    apply_business: applyBusiness,
                    apply_compliance: applyCompliance,
                    apply_faq: applyFaq,
                  })
                }
                disabled={applyMutation.isPending || (!applyBusiness && !applyCompliance && !applyFaq)}
                data-testid="extraction-merge-btn"
              >
                <Merge className="h-3 w-3 mr-1" />
                Merge with Existing
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-3 text-[11px] text-slate-500 hover:text-slate-300"
                onClick={() =>
                  applyMutation.mutate({
                    mode: "dismiss",
                    apply_business: false,
                    apply_compliance: false,
                    apply_faq: false,
                  })
                }
                disabled={applyMutation.isPending}
                data-testid="extraction-dismiss-btn"
              >
                Dismiss
              </Button>
            </div>
          </div>
        )}

        {alreadyApplied && (
          <div className="flex items-center gap-2 pt-2 border-t border-white/[0.04]">
            <Check className="h-3.5 w-3.5 text-emerald-400" />
            <span className="text-xs text-emerald-400">
              Extraction applied to notes
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── ExtractedList ────────────────────────────────────────────────────────
function ExtractedList({ title, items, isCompliance }) {
  if (!items?.length) return null;
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] font-medium text-slate-400">{title}</span>
        {isCompliance && (
          <AlertTriangle className="h-2.5 w-2.5 text-amber-500" />
        )}
      </div>
      <ul className="space-y-0.5">
        {items.map((item, i) => (
          <li
            key={i}
            className={cn(
              "text-[11px] leading-relaxed pl-2 border-l-2",
              isCompliance
                ? "text-amber-300/80 border-amber-500/30"
                : "text-slate-400 border-slate-700/50"
            )}
          >
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── SuggestedNote ────────────────────────────────────────────────────────
function SuggestedNote({ label, text, checked, onToggle, isCompliance, testId }) {
  return (
    <div
      className={cn(
        "rounded-md border p-2.5 transition-colors",
        checked
          ? isCompliance
            ? "border-amber-500/20 bg-amber-500/[0.04]"
            : "border-violet-500/15 bg-violet-500/[0.03]"
          : "border-slate-700/20 bg-slate-800/20 opacity-60"
      )}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-medium text-slate-300">{label}</span>
          {isCompliance && (
            <Badge
              variant="secondary"
              className="h-3.5 px-1 text-[8px] bg-amber-500/15 text-amber-400 border-0"
            >
              Review Required
            </Badge>
          )}
        </div>
        <button
          onClick={onToggle}
          className={cn(
            "h-4 w-8 rounded-full relative transition-colors",
            checked ? "bg-violet-500" : "bg-slate-700"
          )}
          data-testid={testId}
        >
          <div
            className={cn(
              "absolute top-0.5 h-3 w-3 rounded-full bg-white transition-transform",
              checked ? "translate-x-4" : "translate-x-0.5"
            )}
          />
        </button>
      </div>
      <p className="text-[11px] text-slate-400 leading-relaxed">{text}</p>
      {isCompliance && checked && (
        <p className="text-[10px] text-amber-400/70 mt-1.5 flex items-center gap-1">
          <AlertTriangle className="h-2.5 w-2.5" />
          Compliance content will be applied as a suggestion — always verify before use.
        </p>
      )}
    </div>
  );
}
